import * as Sentry from '@sentry/react';

// console output is for humans debugging non-prod environments; production stays silent (Sentry
// still receives everything). The app layer flips this at bootstrap (App.tsx) — shared code can't
// read the environment config itself without breaking the layer boundary.
let consoleEnabled = true;

export function setLoggerConsoleEnabled(enabled: boolean) {
  consoleEnabled = enabled;
}

export const LEVELS = {
  Info: 'ℹ️',
  Bug: '🐛',
  Json: '🔄',
  Warning: '‼️',
  Error: '❌',
  Fatal: '⛔️',
  Network: '🌐',
  NetworkError: '🌐❌',
  Media: '🎥',
  MediaNetwork: '🌐🎥',
  ParamReqError: '⬆️❌',
  Debug: '🛠️',
  Database: '💽',
  Function: 'Ⓜ️'
} as const;

export type Level = keyof typeof LEVELS;

export interface SentryLogOptions {
  level?: Level;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  breadcrumbs?: Sentry.Breadcrumb[];
}

export interface TraceOptions extends SentryLogOptions {
  tag?: string;
  sentryLogging?: boolean;
}

const SENTRY_LEVELS = {
  Info: 'info',
  Bug: 'debug',
  Json: 'debug',
  Warning: 'warning',
  Error: 'error',
  Fatal: 'fatal',
  Network: 'debug',
  NetworkError: 'error',
  Media: 'info',
  MediaNetwork: 'debug',
  ParamReqError: 'warning',
  Debug: 'debug',
  Database: 'info',
  Function: 'debug'
} as const;

function getSymbol(level: Level) {
  return LEVELS[level];
}

function getTimeStamp(): string {
  return new Date().toISOString();
}

export function captureSentryMessage(message: string, options: SentryLogOptions) {
  const { level = 'Info', tags = {}, extra: _extra = {}, breadcrumbs = [] } = options;
  const extra = { ..._extra, loggedAt: getTimeStamp() };

  if (breadcrumbs?.length) {
    for (const crumb of breadcrumbs) {
      Sentry.addBreadcrumb(crumb);
    }
  }

  Sentry.withScope(scope => {
    if (tags) {
      Object.entries(tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }

    if (extra) {
      Object.entries(extra).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }

    scope.setLevel(SENTRY_LEVELS[level ?? 'Info']);
    Sentry.captureMessage(message);
  });
}

export const Logger = {
  trace(message: string, options: TraceOptions = {}) {
    const { level = 'Info', tag, sentryLogging = false, tags, extra, breadcrumbs } = options;

    const emoji = getSymbol(level);
    if (consoleEnabled) {
      const formatted = `[${emoji} ${tag ?? level}] ⏱️ ${getTimeStamp()} 📜 ${message}]`.trim();
      console.info(formatted);
    }

    if (sentryLogging) {
      captureSentryMessage(`${tag ?? level}: ${message}`, {
        level,
        tags,
        extra,
        breadcrumbs
      });
    }
  },

  stackTrace(title = 'StackTrace') {
    if (!consoleEnabled) {
      return;
    }
    console.info(`************* Stacktrace for ${title}: *************`);
    // eslint-disable-next-line no-console -- console.trace is the whole point of this helper
    console.trace();
    console.info('************* End of stacktrace. *************');
  }
};

export default Logger;
